'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

interface SystemSettings {
  currencySymbol: string
  platformName: string
}

interface UserContextType extends SystemSettings {
  loading: boolean
  refreshSettings: () => Promise<void>
}

const UserContext = createContext<UserContextType>({
  currencySymbol: '¥',
  platformName: '',
  loading: true,
  refreshSettings: async () => {}
})

export function UserProvider({ children }: { children: ReactNode }) {
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    currencySymbol: '¥',
    platformName: ''
  })
  const [loading, setLoading] = useState(true)

  // 获取系统设置的函数
  const fetchSystemSettings = async () => {
    try {
      const res = await fetch('/api/system/settings')
      const data = await res.json()
      if (data.currencySymbol) {
        setSystemSettings({
          currencySymbol: data.currencySymbol,
          platformName: data.platformName || ''
        })
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error)
    } finally {
      setLoading(false)
    }
  }

  // 初始加载系统设置
  useEffect(() => {
    fetchSystemSettings()
  }, [])

  return (
    <UserContext.Provider value={{ 
      currencySymbol: systemSettings.currencySymbol, 
      platformName: systemSettings.platformName,
      loading,
      refreshSettings: fetchSystemSettings
    }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

// 货币格式化工具函数
export function useFormatCurrency() {
  const { currencySymbol } = useUser()
  
  return (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return `${currencySymbol}0.00`
    }
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(num)) {
      return `${currencySymbol}0.00`
    }
    
    // 金额截断到2位小数
    const truncated = Math.floor(num * 100) / 100
    return `${currencySymbol}${truncated.toFixed(2)}`
  }
}

// 价格格式化工具函数（保持原始精度）
export function useFormatPrice() {
  const { currencySymbol } = useUser()
  
  return (price: number | string | null | undefined) => {
    if (price === null || price === undefined) {
      return `${currencySymbol}0`
    }
    const num = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(num)) {
      return `${currencySymbol}0`
    }
    
    // 价格保持原始精度
    return `${currencySymbol}${num}`
  }
}